import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { faArrowLeft, faBarcode, faCheck, faFileLines, faMagnifyingGlass, faTruck } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { FilterSettingsModel, GridComponent, PageSettingsModel, SelectionSettingsModel } from '@syncfusion/ej2-angular-grids';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { ConfirmationDialogComponent } from '../../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { NavigationService } from '../../../../../core/services/navigation.service';
import { InventoryService } from '../../../../pick-plan/services/inventory.service';
import { HandlingUnitBulkMoveToOrder } from '../../../models/handling-unit-bulk-move-to-order';
import { HandlingUnitMasterDTO, handlingUnitLocation } from '../../../models/handling-unit-master';
import { KW_MOBILE_QUERY } from '../../../models/kw-breakpoints';
import { InventorySessionService } from '../../../services/inventory-session.service';

@Component({
  selector: 'inventory-bulk-move-to-order',
  templateUrl: './inventory-bulk-move-to-order.component.html',
  styleUrls: ['./inventory-bulk-move-to-order.component.scss']
})
export class InventoryBulkMoveToOrderComponent implements OnInit {
  @ViewChild('grid') grid: GridComponent;

  // Icons
  faArrowLeft = faArrowLeft;
  faBarcode = faBarcode;
  faCheck = faCheck;
  faFileLines = faFileLines;
  faMagnifyingGlass = faMagnifyingGlass;
  faTruck = faTruck;

  public mobileVisibleCount = 50;
  private gridSelectedCount = 0;
  // The grid is hidden below KW_MOBILE_QUERY; the card list keeps its own selection.
  private mobileSelection = new Set<number>();

  private destroyRef = inject(DestroyRef);
  public isLoading: boolean = false;
  public selectionOptions: SelectionSettingsModel;
  public selectedRecords: HandlingUnitBulkMoveToOrder[] = [];
  public moveCount: number = 0;
  public orderNumberSearch: string = "";
  public selectedOrder: any = null;

  public pageSettings: PageSettingsModel = {
    pageSize: 200,
    pageSizes: [50, 100, 200],
  };

  public filterSettings: FilterSettingsModel = {
    mode: 'Immediate',
    type: 'Excel',
  };

  constructor(
    public session: InventorySessionService,
    private errorService: ErrorService,
    private router: Router,
    private toastr: NbToastrService,
    private inventoryService: InventoryService,
    private dialog: MatDialog,
    private navigationService: NavigationService,
    private breakpoints: BreakpointObserver
  ) {}

  ngOnInit() {
    this.selectionOptions = {
      type: 'Multiple',
      checkboxOnly: true,
      checkboxMode: 'Default',
    };

    this.isLoading = true;

    // Check if session data exists
    if (!this.session.handlingUnitBulkMoveList || this.session.handlingUnitBulkMoveList.length <= 0) {
      this.router.navigateByUrl('/harvest-engine/inventory/inventory-summary');
      return;
    }

    // Initialize quantity fields with 0 first so grid can render
    this.session.handlingUnitBulkMoveList.forEach((item: any) => {
      if (!item.quantity) {
        item.quantity = 0;
      }
      if (!item.availableQuantity) {
        item.availableQuantity = 0;
      }
    });

    this.moveCount = this.session.handlingUnitBulkMoveList.length;

    // Load HUM data to get actual quantities
    this.loadInventoryData();
  }

  loadInventoryData() {
    if (!this.session.handlingUnitBulkMoveList || this.session.handlingUnitBulkMoveList.length === 0) {
      this.isLoading = false;
      return;
    }

    const ids: number[] = this.session.handlingUnitBulkMoveList
      .map((x) => x.humId)
      .filter((v) => v != null && v != undefined);

    if (ids.length === 0) {
      this.isLoading = false;
      return;
    }

    this.inventoryService.getAllHUMbyId(ids).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: HandlingUnitMasterDTO[]) => {
        if (data && data.length > 0) {
          this.initializeBulkMoveData(data);
          this.moveCount = this.session.handlingUnitBulkMoveList.length;
        } else {
          this.moveCount = 0;
        }
        this.isLoading = false;
      },
      (err) => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    );
  }

  initializeBulkMoveData(humData: HandlingUnitMasterDTO[]) {
    if (!this.session.handlingUnitBulkMoveList || this.session.handlingUnitBulkMoveList.length === 0) {
      return;
    }

    // Add quantity data to the existing session list items
    this.session.handlingUnitBulkMoveList.forEach((item: any) => {
      const humDetails = humData.find((h) => h.id === item.humId);
      let totalQuantity = 0;

      if (humDetails && humDetails.handlingUnitDetails && humDetails.handlingUnitDetails.length > 0) {
        totalQuantity = humDetails.handlingUnitDetails.reduce((sum, detail) => {
          return sum + (detail.quantity ? detail.quantity : 0);
        }, 0);
      }

      item.quantity = totalQuantity;
      item.availableQuantity = totalQuantity;
    });

    // Refresh grid to show updated quantities
    if (this.grid) {
      this.grid.refresh();
    }
  }

  searchOrder() {
    if (!this.orderNumberSearch || this.orderNumberSearch.trim() === '') {
      this.toastr.warning('Please enter an order barcode', 'Warning');
      return;
    }

    this.isLoading = true;
    this.inventoryService.getInventoryHUMByBarcode(this.orderNumberSearch.trim())
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (hum) => {
          if (hum && hum.id) {
            this.selectedOrder = hum;
            this.toastr.success('Order HUM found: ' + hum.barcode, 'Success');
          } else {
            this.selectedOrder = null;
            this.toastr.warning('No order HUM found with that barcode', 'Warning');
          }
          this.isLoading = false;
        },
        (err) => {
          this.selectedOrder = null;
          this.toastr.danger('Order HUM not found or error occurred', 'Error');
          this.isLoading = false;
          this.errorService.handleError(err);
        }
      );
  }

  assignToOrder() {
    if (!this.selectedOrder) {
      this.toastr.warning('Please search and select an order first', 'Warning');
      return;
    }

    // Get batch changes if any
    const batchChanges: any = this.grid?.editModule ? this.grid.editModule.getBatchChanges() : { changedRecords: [] };
    

    // Apply batch changes to the data source
    if (batchChanges.changedRecords && batchChanges.changedRecords.length > 0) {
      batchChanges.changedRecords.forEach((changedRecord: any) => {
        const itemInList: any = this.session.handlingUnitBulkMoveList.find((item: any) => item.humId === changedRecord.humId);
        if (itemInList) {
          itemInList.quantity = changedRecord.quantity;
        }
      });
    }

    const selectedRecords = this.selectedUnits();
    const itemsToAssign = selectedRecords.length > 0 ? selectedRecords : this.units;

    

    // Validation
    let hasInvalidQuantity = false;
    let hasExceededQuantity = false;

    for (const item of itemsToAssign) {
      if (!item.quantity || item.quantity <= 0) {
        hasInvalidQuantity = true;
        break;
      }
      if (item.availableQuantity && item.quantity > item.availableQuantity) {
        hasExceededQuantity = true;
        break;
      }
    }

    if (hasInvalidQuantity) {
      this.toastr.warning('All items must have a quantity greater than 0', 'Warning');
      return;
    }

    if (hasExceededQuantity) {
      this.toastr.warning('Quantity to assign cannot exceed available quantity', 'Warning');
      return;
    }

    const confirmMessage = 'Are you sure you want to assign ' + itemsToAssign.length +
      ' item(s) to order HUM ' + this.selectedOrder.barcode + '?';

    this.dialog
      .open(ConfirmationDialogComponent, {
        minWidth: '10%',
        data: {
          confirmLabel: 'Yes',
          title: 'Confirm Assign to Order',
          confirmMessage: confirmMessage,
          cancelLabel: 'No',
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
        if (result === true) {
          this.executeAssignment(itemsToAssign);
        }
      });
  }

  executeAssignment(itemsToAssign: any[]) {
    this.isLoading = true;

    

    // Get full HUM data for items being moved to extract detail IDs
    const sourceHumIds = itemsToAssign.map((item) => item.humId);

    this.inventoryService.getAllHUMbyId(sourceHumIds).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (sourceHums: HandlingUnitMasterDTO[]) => {
        const handlingUnitDetailIds: number[] = [];
        const quantities: number[] = [];

        // Build arrays of detail IDs and quantities
        sourceHums.forEach((sourceHum) => {
          const itemToMove = itemsToAssign.find((i) => i.humId === sourceHum.id);
          let remainingQuantityToMove = itemToMove.quantity;

          

          if (sourceHum.handlingUnitDetails && sourceHum.handlingUnitDetails.length > 0) {
            // Filter only active details
            const activeDetails = sourceHum.handlingUnitDetails.filter(d => d.isActive);

            

            for (let i = 0; i < activeDetails.length && remainingQuantityToMove > 0; i++) {
              const detail = activeDetails[i];
              const detailQuantity = detail.quantity || 0;
              const quantityFromThisDetail = Math.min(remainingQuantityToMove, detailQuantity);

              

              if (quantityFromThisDetail > 0) {
                handlingUnitDetailIds.push(detail.id);
                quantities.push(quantityFromThisDetail);
                remainingQuantityToMove = remainingQuantityToMove - quantityFromThisDetail;
              }
            }
          }
        });

        

        // Call the new API endpoint
        const request = {
          handlingUnitDetailIds: handlingUnitDetailIds,
          quantities: quantities,
          orderNumber: this.selectedOrder.barcode
        };

        this.inventoryService.moveToOrderHUM(request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          () => {
            this.toastr.success('Items successfully moved to order', 'SUCCESS');
            this.isLoading = false;
            this.router.navigateByUrl('/harvest-engine/inventory/inventory-summary');
          },
          (err) => {
            this.isLoading = false;
            this.errorService.handleError(err);
          }
        );
      },
      (err) => {
        this.isLoading = false;
        this.errorService.handleError(err);
      }
    );
  }

  onCellSave(args: any) {
    if (args.columnName === 'quantity') {
      const item = args.data as any;
      if (item.availableQuantity && args.value > item.availableQuantity) {
        this.toastr.warning('Quantity cannot exceed available quantity', 'Warning');
        args.cancel = true;
      }
    }
  }

  back() {
    this.navigationService.back();
  }

  /** The session list, carrying the quantity fields initializeBulkMoveData adds. */
  get units(): HandlingUnitBulkMoveToOrder[] {
    return this.session.handlingUnitBulkMoveList ?? [];
  }

  private get selectedCount(): number {
    return this.breakpoints.isMatched(KW_MOBILE_QUERY) ? this.mobileSelection.size : this.gridSelectedCount;
  }

  get selectionLabel(): string {
    return this.selectedCount ? `${this.selectedCount} selected` : 'None selected · assigns all';
  }

  get summary(): string {
    if (!this.selectedOrder)
      return 'Find an order to continue';
    const count = this.selectedCount || this.session.handlingUnitBulkMoveList?.length || 0;
    return `Assigning ${count} ${count === 1 ? 'unit' : 'units'} to order ${this.selectedOrder.barcode}`;
  }

  onGridSelectionChange() {
    this.gridSelectedCount = this.grid?.getSelectedRecords().length ?? 0;
  }

  isMobileSelected(unit: HandlingUnitBulkMoveToOrder): boolean {
    return this.mobileSelection.has(unit.humId);
  }

  toggleMobileSelection(unit: HandlingUnitBulkMoveToOrder) {
    if (!this.mobileSelection.delete(unit.humId))
      this.mobileSelection.add(unit.humId);
  }

  unitLocation(unit: HandlingUnitBulkMoveToOrder): string {
    return handlingUnitLocation(unit);
  }

  trackByUnit(_: number, unit: HandlingUnitBulkMoveToOrder): number | undefined {
    return unit.humId;
  }

  private selectedUnits(): HandlingUnitBulkMoveToOrder[] {
    if (this.breakpoints.isMatched(KW_MOBILE_QUERY))
      return this.units.filter(x => this.mobileSelection.has(x.humId));
    return (this.grid?.getSelectedRecords() ?? []) as HandlingUnitBulkMoveToOrder[];
  }
}
